import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { CAREER_STAGE_LABELS, Institution, Profile } from '../types';

// ─── Stage inference (product decision 2026-04-29) ───────────────────────────
// Stage 1: assessment_status = 'completed' → Completed
// Stage 2: transition_analysis IS NOT NULL → Completed
// Stage 3 & 4: both 1+2 done → Active; else —
function stageDisplay(a: { assessment_status: string; transition_analysis: any }) {
  const s1 = a.assessment_status === 'completed';
  const s2 = a.transition_analysis != null;
  const active = s1 && s2;
  return {
    stage1: s1 ? 'Completed' : 'Pending',
    stage2: s2 ? 'Completed' : s1 ? 'In Progress' : '—',
    stage3: active ? 'Active' : '—',
    stage4: active ? 'Active' : '—',
  };
}

// ─── CSV helper ───────────────────────────────────────────────────────────────
function downloadCSV(rows: Record<string, any>[], filename: string) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const esc = (v: any) => {
    const s = v == null ? '' : String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n')
      ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers.join(','), ...rows.map(r => headers.map(h => esc(r[h])).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

// ─── Cohort summary text ──────────────────────────────────────────────────────
function cohortSummary(
  name: string, total: number, s1: number, s2: number,
  active: number, weekly: number, archetypes: Record<string, number>
): string {
  const p = (n: number) => total ? `${Math.round(n / total * 100)}%` : '0%';
  const topArch = Object.entries(archetypes).sort((a, b) => b[1] - a[1])[0];
  const profileWord = topArch
    ? ({ 'The Commander': 'leadership-forward', 'The Architect': 'systems-thinking', 'The Catalyst': 'catalyst-driven' } as any)[topArch[0]] || 'results-oriented'
    : null;

  const line1 = `${name} has ${total} athlete${total !== 1 ? 's' : ''} enrolled on the BTHD platform, with ${s1} (${p(s1)}) completing the Identity Assessment and ${s2} (${p(s2)}) receiving a personalized Transition Analysis.`;
  const line2 = topArch && profileWord
    ? `The ${topArch[0]} archetype is most prevalent at ${s1 ? Math.round(topArch[1] / s1 * 100) : 0}% of assessed athletes, reflecting a ${profileWord} cohort profile.`
    : s1 > 0
    ? 'Archetype data is being collected as athletes progress through the Identity Assessment.'
    : 'Encourage athletes to begin the BTHD journey by completing their Identity Assessment.';
  const line3 = active > 0
    ? `${active} athlete${active !== 1 ? 's are' : ' is'} actively engaged across all four BTHD stages, with ${weekly} showing platform activity in the past 7 days.`
    : weekly > 0
    ? `${weekly} athlete${weekly !== 1 ? 's are' : ' is'} active this week as the cohort progresses through the assessment stages.`
    : 'Encourage athletes to complete the Identity Assessment to unlock their personalized transition and leadership pathway.';

  return `${line1} ${line2} ${line3}`;
}

// ─── PDF HTML builder ─────────────────────────────────────────────────────────
function buildInstitutionPDF(inst: Institution, athletes: Profile[]): string {
  const today = new Date().toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' });
  const total = athletes.length;
  const s1Count = athletes.filter(a => a.assessment_status === 'completed').length;
  const s2Count = athletes.filter(a => a.transition_analysis != null).length;
  const activeCount = athletes.filter(a => a.assessment_status === 'completed' && a.transition_analysis != null).length;
  const sevenAgo = Date.now() - 7 * 86400000;
  const weeklyActive = athletes.filter(a => a.updated_at && new Date(a.updated_at).getTime() > sevenAgo).length;
  const pct = (n: number) => total ? `${Math.round(n / total * 100)}%` : '—';
  const archetypes: Record<string, number> = {};
  athletes.forEach(a => { if (a.athlete_archetype) archetypes[a.athlete_archetype] = (archetypes[a.athlete_archetype] || 0) + 1; });
  const tierColor = inst.license_tier === 'elite' ? '#014c8d' : inst.license_tier === 'pro' ? '#0fa86d' : '#e88c0e';

  const stageBadge = (s: string) => {
    if (s === 'Completed') return `<span class="b bg">✓ Completed</span>`;
    if (s === 'Active')    return `<span class="b bb">● Active</span>`;
    if (s === 'In Progress') return `<span class="b bo">In Progress</span>`;
    return `<span style="color:#94a3b8">—</span>`;
  };

  const athleteRows = athletes.map(a => {
    const st = stageDisplay(a);
    const lastActive = a.updated_at ? new Date(a.updated_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }) : '—';
    return `<tr>
      <td><strong>${(a.full_name || a.username || 'Unknown').slice(0, 28)}</strong></td>
      <td>${a.sport || '—'}</td>
      <td>${a.athlete_archetype || '—'}</td>
      <td style="text-align:center">${stageBadge(st.stage1)}</td>
      <td style="text-align:center">${stageBadge(st.stage2)}</td>
      <td style="text-align:center">${stageBadge(st.stage3)}</td>
      <td style="text-align:center">${stageBadge(st.stage4)}</td>
      <td style="color:#64748b;font-size:11px">${lastActive}</td>
    </tr>`;
  }).join('');

  const archetypeRows = Object.entries(archetypes).sort((a, b) => b[1] - a[1]).map(([arch, count]) =>
    `<tr><td>${arch}</td><td style="text-align:center;font-weight:700">${count}</td><td style="text-align:center">${s1Count ? Math.round(count / s1Count * 100) + '%' : '—'}</td></tr>`
  ).join('');

  const stageRows = [
    { label: '01 — Identity Assessment',   count: s1Count,    note: 'Completed', cls: 'bg' },
    { label: '02 — Transition Analysis',   count: s2Count,    note: 'Completed', cls: 'bg' },
    { label: '03 — Leadership Development', count: activeCount, note: 'Active',   cls: 'bb' },
    { label: '04 — Legacy Building',        count: activeCount, note: 'Active',   cls: 'bb' },
  ].map(s => `<tr>
    <td>${s.label}</td>
    <td style="text-align:center;font-weight:700">${s.count}</td>
    <td style="text-align:center">${pct(s.count)}</td>
    <td style="text-align:center"><span class="b ${s.cls}">${s.note}</span></td>
  </tr>`).join('');

  const summary = cohortSummary(inst.name, total, s1Count, s2Count, activeCount, weeklyActive, archetypes);
  const periodStr = inst.license_start_date
    ? `${new Date(inst.license_start_date).toLocaleDateString('en-CA', { month: 'short', year: 'numeric' })} — ${inst.license_end_date ? new Date(inst.license_end_date).toLocaleDateString('en-CA', { month: 'short', year: 'numeric' }) : 'Ongoing'}`
    : '—';

  return `<!DOCTYPE html><html lang="en"><head>
<meta charset="UTF-8">
<title>BTHD Report — ${inst.name}</title>
<link href="https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;800;900&family=Barlow:wght@400;600;700&display=swap" rel="stylesheet">
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Barlow',sans-serif;color:#0a0f1a;background:#fff;padding:40px 48px;font-size:13px;line-height:1.5}
.bc{font-family:'Barlow Condensed',sans-serif}
.hd{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:20px;border-bottom:3px solid #bf1e2e;margin-bottom:28px}
.brand{font-family:'Barlow Condensed',sans-serif;font-weight:900;font-size:10px;color:#bf1e2e;letter-spacing:3px;text-transform:uppercase;margin-bottom:6px}
h1{font-family:'Barlow Condensed',sans-serif;font-weight:900;font-size:30px;color:#0a0f1a;line-height:1.1;margin-bottom:2px}
h2{font-family:'Barlow Condensed',sans-serif;font-weight:800;font-size:13px;color:#0a0f1a;letter-spacing:1.5px;text-transform:uppercase}
.meta{text-align:right;font-size:12px;color:#64748b;line-height:2}
.meta strong{color:#0a0f1a}
.dg{display:grid;grid-template-columns:repeat(4,1fr);gap:1px;background:#e2e8f0;border:1px solid #e2e8f0;border-radius:10px;overflow:hidden;margin-bottom:28px}
.dc{background:#f8fafc;padding:12px 16px}
.dl{font-size:9px;font-weight:700;color:#94a3b8;letter-spacing:1.5px;text-transform:uppercase;margin-bottom:4px}
.dv{font-family:'Barlow Condensed',sans-serif;font-weight:800;font-size:20px;color:#0a0f1a}
.ds{font-size:11px;color:#64748b;margin-top:1px}
.sg{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:28px}
.sc{border:1px solid #e2e8f0;border-radius:10px;padding:16px;border-top:3px solid #bf1e2e}
.sv{font-family:'Barlow Condensed',sans-serif;font-weight:900;font-size:32px;color:#0a0f1a;line-height:1;margin-bottom:4px}
.sl{font-size:10px;font-weight:700;color:#64748b;letter-spacing:1px;text-transform:uppercase}
.sec{margin-bottom:28px}
.sh{display:flex;align-items:center;gap:10px;padding-bottom:8px;border-bottom:2px solid #0a0f1a;margin-bottom:14px}
.dot{width:8px;height:8px;border-radius:50%;background:#bf1e2e;flex-shrink:0}
table{width:100%;border-collapse:collapse}
th{font-size:9px;font-weight:700;color:#94a3b8;letter-spacing:1.5px;text-transform:uppercase;padding:8px 10px;border-bottom:2px solid #e2e8f0;text-align:left}
td{padding:7px 10px;border-bottom:1px solid #f1f5f9;font-size:12px;vertical-align:middle}
tr:last-child td{border-bottom:none}
tr:nth-child(even) td{background:#f8fafc}
.b{display:inline-block;padding:2px 7px;border-radius:20px;font-size:10px;font-weight:700}
.bg{background:#dcfce7;color:#16a34a}
.bb{background:#dbeafe;color:#1d4ed8}
.bo{background:#fed7aa;color:#c2410c}
.cb{background:#f8fafc;border:1px solid #e2e8f0;border-left:4px solid #014c8d;border-radius:8px;padding:16px 20px;margin-bottom:28px;font-size:13px;line-height:1.8;color:#334155}
.ft{margin-top:36px;padding-top:16px;border-top:1px solid #e2e8f0;display:flex;justify-content:space-between;font-size:10px;color:#94a3b8}
.print-btn{position:fixed;top:20px;right:20px;background:#bf1e2e;color:#fff;border:none;padding:11px 22px;border-radius:8px;font-family:'Barlow',sans-serif;font-size:13px;font-weight:700;cursor:pointer;letter-spacing:0.5px;box-shadow:0 4px 12px rgba(191,30,46,0.3)}
@media print{.print-btn{display:none}body{padding:20px 24px}.pb{page-break-before:always}}
</style></head><body>

<button class="print-btn" onclick="window.print()">↓ Save as PDF</button>

<div class="hd">
  <div>
    <div class="brand">Bigger Than Hoop Dreams — Institution Report</div>
    <h1>${inst.name}</h1>
    <div style="font-size:13px;color:#64748b;margin-top:4px">${inst.type}${inst.state_province ? ` · ${inst.state_province},` : ''} ${inst.country}</div>
  </div>
  <div class="meta">
    <div>Report Date: <strong>${today}</strong></div>
    <div>License Period: <strong>${periodStr}</strong></div>
    ${inst.license_tier ? `<div>Tier: <strong style="color:${tierColor};text-transform:uppercase">${inst.license_tier}</strong></div>` : ''}
    <div>Status: <strong style="color:${inst.status === 'active' ? '#16a34a' : '#94a3b8'}">${inst.status.toUpperCase()}</strong></div>
  </div>
</div>

<div class="dg">
  <div class="dc"><div class="dl">Athletes Enrolled</div><div class="dv">${total}${inst.max_athletes ? ` / ${inst.max_athletes}` : ''}</div><div class="ds">Registered on platform</div></div>
  <div class="dc"><div class="dl">License Fee</div><div class="dv">${inst.license_fee != null ? `$${inst.license_fee.toLocaleString()}` : '—'}</div><div class="ds">${inst.currency || 'CAD'}</div></div>
  <div class="dc"><div class="dl">Contact</div><div class="dv" style="font-size:15px">${inst.contact_name || '—'}</div><div class="ds">${inst.contact_email || ''}</div></div>
  <div class="dc"><div class="dl">Active This Week</div><div class="dv">${weeklyActive}</div><div class="ds">Last 7 days</div></div>
</div>

<div class="sg">
  <div class="sc"><div class="sv">${total}</div><div class="sl">Total Athletes</div></div>
  <div class="sc"><div class="sv" style="color:#bf1e2e">${s1Count}</div><div class="sl">Identity Assessed</div></div>
  <div class="sc"><div class="sv" style="color:#014c8d">${s2Count}</div><div class="sl">Transition Analysis</div></div>
  <div class="sc"><div class="sv" style="color:#0fa86d">${activeCount}</div><div class="sl">Fully Engaged</div></div>
</div>

${total > 0 ? `<div class="cb">${summary}</div>` : ''}

<div class="sec">
  <div class="sh"><div class="dot"></div><h2>Stage Completion Overview</h2></div>
  <table><thead><tr><th>Stage</th><th style="text-align:center">Athletes</th><th style="text-align:center">% of Cohort</th><th style="text-align:center">Status</th></tr></thead>
  <tbody>${stageRows}</tbody></table>
</div>

${archetypeRows ? `<div class="sec">
  <div class="sh"><div class="dot"></div><h2>Archetype Distribution</h2></div>
  <table><thead><tr><th>Archetype</th><th style="text-align:center">Athletes</th><th style="text-align:center">% of Assessed</th></tr></thead>
  <tbody>${archetypeRows}</tbody></table>
</div>` : ''}

${athletes.length > 0 ? `<div class="sec ${athletes.length > 14 ? 'pb' : ''}">
  <div class="sh"><div class="dot"></div><h2>Individual Athlete Progress</h2></div>
  <table><thead><tr><th>Name</th><th>Sport</th><th>Archetype</th><th style="text-align:center">Stage 01</th><th style="text-align:center">Stage 02</th><th style="text-align:center">Stage 03</th><th style="text-align:center">Stage 04</th><th>Last Active</th></tr></thead>
  <tbody>${athleteRows}</tbody></table>
</div>` : ''}

<div class="ft">
  <span>BTHD — Bigger Than Hoop Dreams · Kitchener, Ontario, Canada · bthd.app</span>
  <span>Confidential · Generated ${today}</span>
</div>

</body></html>`;
}

// ─── Main Component ───────────────────────────────────────────────────────────

interface AiAgg { functionName: string; totalMessages: number; uniqueUsers: Set<string>; lastUsed: string; }

export default function ReportsPage() {
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [athletes, setAthletes] = useState<Profile[]>([]);
  const [aiUsage, setAiUsage] = useState<any[]>([]);
  const [selectedInstId, setSelectedInstId] = useState<string>('all');
  const [csvInstId, setCsvInstId] = useState<string>('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const [{ data: insts }, { data: aths }, { data: usage }] = await Promise.all([
        supabase.from('institutions').select('*').order('name'),
        supabase.from('profiles')
          .select('id,full_name,username,sport,career_stage,athlete_archetype,assessment_status,transition_analysis,subscription_tier,updated_at,institution_id')
          .eq('is_admin', false).eq('user_type', 'athlete')
          .order('updated_at', { ascending: false }),
        supabase.from('ai_usage').select('user_id,function_name,message_count,usage_date').order('usage_date', { ascending: false }),
      ]);
      setInstitutions((insts as Institution[]) ?? []);
      setAthletes((aths as Profile[]) ?? []);
      setAiUsage(usage ?? []);
      setLoading(false);
    }
    load();
  }, []);

  // ── Derived data ──────────────────────────────────────────────────────────

  const pdfAthletes = selectedInstId === 'all'
    ? athletes
    : athletes.filter(a => a.institution_id === selectedInstId);

  const csvAthletes = csvInstId === 'all'
    ? athletes
    : athletes.filter(a => a.institution_id === csvInstId);

  const selectedInst = institutions.find(i => i.id === selectedInstId);

  // AI usage aggregations
  const aiByFunction: Record<string, AiAgg> = {};
  aiUsage.forEach(row => {
    if (!aiByFunction[row.function_name]) {
      aiByFunction[row.function_name] = { functionName: row.function_name, totalMessages: 0, uniqueUsers: new Set(), lastUsed: row.usage_date };
    }
    aiByFunction[row.function_name].totalMessages += row.message_count ?? 1;
    aiByFunction[row.function_name].uniqueUsers.add(row.user_id);
    if (row.usage_date > aiByFunction[row.function_name].lastUsed) aiByFunction[row.function_name].lastUsed = row.usage_date;
  });
  const aiRows = Object.values(aiByFunction).sort((a, b) => b.totalMessages - a.totalMessages);
  const totalMessages = aiRows.reduce((s, r) => s + r.totalMessages, 0);
  const totalUniqueUsers = new Set(aiUsage.map(r => r.user_id)).size;

  // Last 14-day daily totals
  const dailyTotals: Record<string, number> = {};
  aiUsage.forEach(row => { dailyTotals[row.usage_date] = (dailyTotals[row.usage_date] || 0) + (row.message_count ?? 1); });
  const last14Days = Object.entries(dailyTotals)
    .sort((a, b) => b[0].localeCompare(a[0]))
    .slice(0, 14);

  // ── CSV export ────────────────────────────────────────────────────────────

  function handleExportCSV() {
    const rows = csvAthletes.map(a => {
      const st = stageDisplay(a);
      return {
        'Name': a.full_name || a.username || '',
        'Sport': a.sport || '',
        'Career Stage': CAREER_STAGE_LABELS[a.career_stage || ''] || a.career_stage || '',
        'Archetype': a.athlete_archetype || '',
        'Assessment Status': a.assessment_status || '',
        'Stage 1 — Identity': st.stage1,
        'Stage 2 — Transition': st.stage2,
        'Stage 3 — Leadership': st.stage3,
        'Stage 4 — Legacy': st.stage4,
        'Subscription Tier': a.subscription_tier || '',
        'Last Active': a.updated_at ? new Date(a.updated_at).toLocaleDateString('en-CA') : '',
        'Institution': institutions.find(i => i.id === a.institution_id)?.name || '',
      };
    });
    const suffix = csvInstId !== 'all'
      ? (institutions.find(i => i.id === csvInstId)?.name || csvInstId).replace(/\s+/g, '-').toLowerCase()
      : 'all';
    downloadCSV(rows, `bthd-athletes-${suffix}-${new Date().toISOString().slice(0, 10)}.csv`);
  }

  // ── PDF export ────────────────────────────────────────────────────────────

  function handleDownloadPDF() {
    if (!selectedInst) return;
    const html = buildInstitutionPDF(selectedInst, pdfAthletes);
    const win = window.open('', '_blank', 'width=1100,height=800');
    if (!win) { alert('Enable pop-ups to open the report.'); return; }
    win.document.write(html);
    win.document.close();
  }

  // ── Styles ────────────────────────────────────────────────────────────────

  const selectStyle: React.CSSProperties = {
    background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)',
    borderRadius: 8, padding: '8px 11px', fontSize: 13, cursor: 'pointer',
    outline: 'none', fontFamily: 'Barlow, sans-serif',
  };

  const sectionStyle: React.CSSProperties = {
    background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--border)',
    overflow: 'hidden', marginBottom: 24,
  };

  const headerStyle: React.CSSProperties = {
    padding: '14px 20px', borderBottom: '1px solid var(--border)',
    borderTop: '3px solid #bf1e2e', display: 'flex', alignItems: 'center',
    justifyContent: 'space-between',
  };

  if (loading) {
    return (
      <div>
        <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 28, color: 'var(--text)', marginBottom: 8 }}>Reports & Exports</div>
        <div style={{ color: 'var(--text-3)', fontSize: 13, padding: '40px 0', textAlign: 'center' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 28, color: 'var(--text)' }}>Reports & Exports</div>
        <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 4 }}>Institution PDFs · Athlete CSV · AI Usage</div>
      </div>

      {/* ── SECTION 1: Institution Report PDF ─────────────────────────────── */}
      <div style={sectionStyle}>
        <div style={headerStyle}>
          <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 14, color: 'var(--text)', letterSpacing: 0.5, textTransform: 'uppercase' }}>
            Institution Report (PDF)
          </div>
          <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Branded · Printable · Shareable</span>
        </div>
        <div style={{ padding: '20px' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 260px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-3)', letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 6 }}>Select Institution</div>
              <select style={{ ...selectStyle, width: '100%' }} value={selectedInstId} onChange={e => setSelectedInstId(e.target.value)}>
                <option value="all">— Choose an institution —</option>
                {institutions.map(i => (
                  <option key={i.id} value={i.id}>{i.name} {i.license_tier ? `(${i.license_tier})` : ''}</option>
                ))}
              </select>
            </div>
            <button
              onClick={handleDownloadPDF}
              disabled={selectedInstId === 'all'}
              style={{
                padding: '9px 20px', borderRadius: 8, background: selectedInstId !== 'all' ? '#bf1e2e' : 'var(--surface-2)',
                border: '1px solid', borderColor: selectedInstId !== 'all' ? '#bf1e2e' : 'var(--border)',
                color: selectedInstId !== 'all' ? '#fff' : 'var(--text-3)', fontFamily: 'Barlow Condensed, sans-serif',
                fontWeight: 700, fontSize: 14, cursor: selectedInstId !== 'all' ? 'pointer' : 'not-allowed', letterSpacing: 0.5,
              }}
            >
              ↓ Download PDF Report
            </button>
          </div>

          {selectedInstId !== 'all' && selectedInst && (
            <div style={{ marginTop: 18, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12 }}>
              {[
                { label: 'Athletes', value: pdfAthletes.length },
                { label: 'Assessed', value: pdfAthletes.filter(a => a.assessment_status === 'completed').length },
                { label: 'Transition Done', value: pdfAthletes.filter(a => a.transition_analysis != null).length },
                { label: 'Fully Engaged', value: pdfAthletes.filter(a => a.assessment_status === 'completed' && a.transition_analysis != null).length },
              ].map(s => (
                <div key={s.label} style={{ background: 'var(--surface-2)', borderRadius: 9, padding: '12px 14px', border: '1px solid var(--border)' }}>
                  <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 26, color: 'var(--text)' }}>{s.value}</div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-3)', letterSpacing: 1, textTransform: 'uppercase', marginTop: 2 }}>{s.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── SECTION 2: Athlete CSV Export ─────────────────────────────────── */}
      <div style={sectionStyle}>
        <div style={{ ...headerStyle, borderTopColor: '#014c8d' }}>
          <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 14, color: 'var(--text)', letterSpacing: 0.5, textTransform: 'uppercase' }}>
            Athlete Data Export (CSV)
          </div>
          <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
            {csvAthletes.length} athlete{csvAthletes.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div style={{ padding: '20px' }}>
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 14 }}>
            Includes: Name · Sport · Career Stage · Archetype · Assessment Status · Stage 1–4 · Subscription Tier · Last Active · Institution ID
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 260px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-3)', letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 6 }}>Filter by Institution</div>
              <select style={{ ...selectStyle, width: '100%' }} value={csvInstId} onChange={e => setCsvInstId(e.target.value)}>
                <option value="all">All Institutions ({athletes.length} athletes)</option>
                {institutions.map(i => {
                  const count = athletes.filter(a => a.institution_id === i.id).length;
                  return <option key={i.id} value={i.id}>{i.name} ({count})</option>;
                })}
              </select>
            </div>
            <button
              onClick={handleExportCSV}
              disabled={csvAthletes.length === 0}
              style={{
                padding: '9px 20px', borderRadius: 8, background: csvAthletes.length > 0 ? '#014c8d' : 'var(--surface-2)',
                border: '1px solid', borderColor: csvAthletes.length > 0 ? '#014c8d' : 'var(--border)',
                color: csvAthletes.length > 0 ? '#fff' : 'var(--text-3)', fontFamily: 'Barlow Condensed, sans-serif',
                fontWeight: 700, fontSize: 14, cursor: csvAthletes.length > 0 ? 'pointer' : 'not-allowed', letterSpacing: 0.5,
              }}
            >
              ↓ Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* ── SECTION 3: AI Usage ────────────────────────────────────────────── */}
      <div style={sectionStyle}>
        <div style={{ ...headerStyle, borderTopColor: '#0fa86d' }}>
          <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 14, color: 'var(--text)', letterSpacing: 0.5, textTransform: 'uppercase' }}>
            AI Usage — Platform Wide
          </div>
          <span style={{ fontSize: 11, color: 'var(--text-3)' }}>All time · archetype-coach + transition-advisor</span>
        </div>
        <div style={{ padding: '20px' }}>

          {/* Summary cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 20 }}>
            {[
              { label: 'Total AI Messages', value: totalMessages, color: '#0fa86d' },
              { label: 'Unique Users', value: totalUniqueUsers, color: '#014c8d' },
              { label: 'Most Used', value: aiRows[0]?.functionName?.replace('archetype-', '').replace('transition-', 'transition ') || '—', color: '#e88c0e' },
            ].map(s => (
              <div key={s.label} style={{ background: 'var(--surface-2)', borderRadius: 9, padding: '14px 16px', border: '1px solid var(--border)', borderTop: `3px solid ${s.color}` }}>
                <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: typeof s.value === 'number' ? 30 : 18, color: s.color, lineHeight: 1 }}>{s.value}</div>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-3)', letterSpacing: 1, textTransform: 'uppercase', marginTop: 4 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* By function table */}
          {aiRows.length === 0 ? (
            <div style={{ color: 'var(--text-3)', fontSize: 13, textAlign: 'center', padding: '24px 0' }}>No AI usage recorded yet.</div>
          ) : (
            <div style={{ background: 'var(--surface)', borderRadius: 10, border: '1px solid var(--border)', overflow: 'hidden', marginBottom: 20 }}>
              <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', fontSize: 10, fontWeight: 700, color: 'var(--text-3)', letterSpacing: 1.5, textTransform: 'uppercase' }}>
                Usage by Function
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Function', 'Total Messages', 'Unique Users', 'Last Used'].map(h => (
                      <th key={h} style={{ padding: '8px 16px', fontSize: 10, fontWeight: 700, color: 'var(--text-3)', letterSpacing: 1.2, textTransform: 'uppercase', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {aiRows.map(r => (
                    <tr key={r.functionName}>
                      <td style={{ padding: '10px 16px', fontWeight: 600, fontSize: 13, color: 'var(--text)', borderBottom: '1px solid var(--border)' }}>{r.functionName}</td>
                      <td style={{ padding: '10px 16px', fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 20, color: '#0fa86d', borderBottom: '1px solid var(--border)' }}>{r.totalMessages}</td>
                      <td style={{ padding: '10px 16px', fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 20, color: '#014c8d', borderBottom: '1px solid var(--border)' }}>{r.uniqueUsers.size}</td>
                      <td style={{ padding: '10px 16px', fontSize: 12, color: 'var(--text-3)', borderBottom: '1px solid var(--border)' }}>{r.lastUsed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Recent 14-day activity */}
          {last14Days.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-3)', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 10 }}>Daily Activity (Last 14 Days)</div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', height: 60 }}>
                {last14Days.slice().reverse().map(([date, count]) => {
                  const max = Math.max(...last14Days.map(d => d[1]));
                  const h = max ? Math.round((count / max) * 48) + 4 : 4;
                  return (
                    <div key={date} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                      <div style={{ fontSize: 9, color: 'var(--text-3)', fontWeight: 700 }}>{count}</div>
                      <div style={{ width: '100%', height: h, background: '#0fa86d', borderRadius: '3px 3px 0 0', opacity: 0.85 }} />
                      <div style={{ fontSize: 8, color: 'var(--text-3)', transform: 'rotate(-45deg)', transformOrigin: 'right', whiteSpace: 'nowrap', marginTop: 2 }}>
                        {date.slice(5)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
